// The ranked list. This is the product; the score above it is a summary.
//
// Sorted by money, because a 300% ACOS on ₹400 is a worse ratio and a smaller
// problem than a 90% ACOS on ₹40,000 — and a seller working a list sorted by
// ratio fixes the wrong thing first.
//
// Every row shows the evidence in the seller's own numbers. The rule that
// produced it is never shown: a verdict a seller has to check arithmetic to
// trust is a verdict they do not trust.
import React, { useState } from 'react';
import { FiCheck, FiRotateCcw, FiX } from 'react-icons/fi';
import s from '../AdsHealth.module.css';
import { money, TYPE_LABEL } from '../format';

function Confidence({ value, parts }) {
  const title = Object.entries(parts || {})
    .map(([k, v]) => `${k.replace('_', ' ')}: ${(v * 100).toFixed(0)}%`).join(' · ');
  return (
    <span className={s.confBar} title={title || undefined}>
      <span className={s.confTrack}>
        <span className={s.confFill} style={{ width: `${(value || 0) * 100}%` }} />
      </span>
      {Math.round((value || 0) * 100)}% confident
    </span>
  );
}

export default function Findings({ data, currency, onDismiss, onRestore, busy }) {
  const [priority, setPriority] = useState('all');
  const [showDismissed, setShowDismissed] = useState(false);

  const all = data?.findings || [];
  const open = all.filter((f) => f.status === 'Open');
  const dismissed = all.filter((f) => f.status === 'Dismissed');
  const counts = {
    all: open.length,
    High: open.filter((f) => f.priority === 'High').length,
    Medium: open.filter((f) => f.priority === 'Medium').length,
    Low: open.filter((f) => f.priority === 'Low').length,
  };
  const shown = (showDismissed ? dismissed : open)
    .filter((f) => priority === 'all' || f.priority === priority);

  const resolved = data?.resolved || [];

  if (!all.length && !resolved.length) {
    return (
      <p className={s.empty}>
        Nothing to work through. No check fired above the minimum-data rule —
        {' '}{data?.gates?.note}
      </p>
    );
  }

  return (
    <div>
      <div className={s.filterRow}>
        {['all', 'High', 'Medium', 'Low'].map((p) => (
          <button key={p}
                  className={priority === p && !showDismissed ? s.chipActive : s.chip}
                  onClick={() => { setPriority(p); setShowDismissed(false); }}>
            {p === 'all' ? 'Everything' : p}
            <span className={s.chipCount}>{counts[p]}</span>
          </button>
        ))}
        {dismissed.length > 0 && (
          <button className={showDismissed ? s.chipActive : s.chip}
                  onClick={() => setShowDismissed(!showDismissed)}>
            Dismissed<span className={s.chipCount}>{dismissed.length}</span>
          </button>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#6B7280' }}>
          {money(data?.total_impact, currency)} of measurable money across the open list
        </span>
      </div>

      <div className={s.findingList}>
        {shown.map((f) => (
          <article key={f.id}
                   className={`${s.finding} ${s[`finding_${f.priority}`]} ${f.status === 'Dismissed' ? s.finding_dismissed : ''}`}>
            <div className={s.findingHead}>
              <span className={s.findingType}>{TYPE_LABEL[f.type] || f.type}</span>
              <span className={s.findingEntity}>{f.entity}</span>
              {f.impact != null
                ? <span className={s.findingImpact}>{money(f.impact, currency)}</span>
                : <span className={s.findingNoImpact}>No rupee figure</span>}
            </div>

            <p className={s.findingWhy}>{f.evidence}</p>
            {f.impact_note && (
              <p className={s.findingWhy} style={{ color: '#7A8496', fontSize: 12.5 }}>
                {f.impact_note}
              </p>
            )}
            {f.returned && <div className={s.returnedFlag}>{f.returned_why}</div>}
            {f.stale && (
              <div className={s.returnedFlag}>
                Marked stale — the data behind this is over 48 hours old. The money it
                names was still spent.
              </div>
            )}

            <div className={s.findingMeta}>
              <span>{f.priority} priority</span>
              <Confidence value={f.confidence} parts={f.confidence_parts} />
              <span>
                Fix in {f.route?.label}
                {f.route && !f.route.available ? ' — not built yet' : ''}
              </span>
              {f.prd_defined && (
                <span className={s.prdFlag} title="This rule is defined in the PRD and has not yet moved to the DRD logic batch.">
                  PRD rule
                </span>
              )}
              <span className={s.findingActions}>
                {f.status === 'Dismissed' ? (
                  <button className={s.linkButton} disabled={busy}
                          onClick={() => onRestore(f.id)}>
                    <FiRotateCcw /> Bring back
                  </button>
                ) : (
                  <>
                    {f.route?.path && (
                      <a className={s.linkButton} href={f.route.path}>
                        <FiCheck /> Fix
                      </a>
                    )}
                    <button className={s.linkButton} disabled={busy}
                            onClick={() => onDismiss(f)}>
                      <FiX /> I meant to do that
                    </button>
                  </>
                )}
              </span>
            </div>
          </article>
        ))}
        {!shown.length && (
          <p className={s.empty}>
            {showDismissed ? 'Nothing dismissed.' : `Nothing at ${priority} priority.`}
          </p>
        )}
      </div>

      {resolved.length > 0 && (
        <>
          <h3 className={s.sectionTitle}>Fixed since the last check</h3>
          <p className={s.coverage}>
            These fired last time and no longer do. A finding resolves by its rule going
            quiet, not by anyone marking it done.
          </p>
          <ul className={s.cardList}>
            {resolved.map((r) => (
              <li key={r.id}>
                <span className={s.cardTerm}>
                  {TYPE_LABEL[r.type] || r.type} — {r.entity}
                </span>
                <span className={s.cardFigure}>
                  {r.impact != null ? money(r.impact, currency) : 'no figure'}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {data?.diagnostic_note && (
        <p className={s.coverage} style={{ marginTop: 16 }}>{data.diagnostic_note}</p>
      )}
    </div>
  );
}
